<template>
    <Overlay :show="loadingManifests">
        <div class="manifest-ui import-runner">
            <header class="mb-2">
                <h2 class="mb-25">Run import</h2>
                <p class="text-muted mb-0">
                    Select an active manifest and upload the source file that should be processed.
                </p>
            </header>

            <div class="import-runner__steps mb-2">
                <div
                    v-for="(step, index) in steps"
                    :key="step.title"
                    class="import-runner__step"
                    :class="{
                        'import-runner__step--active': currentStageIndex === index && !isRunnerStepCompleted(index),
                        'import-runner__step--completed': isRunnerStepCompleted(index),
                        'import-runner__step--failed': index === 2 && executionFailed,
                    }"
                >
                    <span class="import-runner__step-marker">
                        <feather-icon v-if="isRunnerStepCompleted(index)" icon="CheckIcon" size="16" />
                        <feather-icon
                            v-else-if="index === 2 && executionFailed"
                            icon="XIcon"
                            size="16"
                        />
                        <template v-else>{{ index + 1 }}</template>
                    </span>
                    <div>
                        <div class="font-weight-bold">{{ step.title }}</div>
                        <small class="text-muted">{{ step.description }}</small>
                    </div>
                </div>
            </div>

            <b-alert v-if="manifestLoadError" variant="danger" show>
                <div class="alert-body d-flex align-items-center justify-content-between">
                    <span>Active import manifests could not be loaded.</span>
                    <b-button size="sm" variant="flat-danger" @click="loadManifests">
                        Retry
                    </b-button>
                </div>
            </b-alert>

            <b-card
                v-if="currentStageIndex === 0"
                title="Prepare import"
                class="import-runner__prepare mb-2"
            >
                <section class="import-runner__prepare-section">
                    <div class="import-runner__section-heading">
                        <span class="import-runner__section-marker">A</span>
                        <div>
                            <h5 class="mb-25">Manifest</h5>
                            <div class="text-muted font-small-2">
                                Choose the active configuration that will interpret the source.
                            </div>
                        </div>
                    </div>

                    <b-row>
                        <b-col v-if="showManifestTypeFilter" md="5">
                            <b-form-group label="Manifest type" label-for="import-manifest-type">
                                <b-form-select
                                    id="import-manifest-type"
                                    v-model="manifestTypeFilter"
                                    :options="manifestTypeOptions"
                                    :disabled="!manifests.length"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col :md="showManifestTypeFilter ? 7 : 12">
                            <b-form-group label="Active manifest" label-for="import-manifest">
                                <b-form-select
                                    id="import-manifest"
                                    v-model="selectedManifestId"
                                    :options="manifestOptions"
                                    :disabled="!filteredManifests.length"
                                    @change="selectManifest"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-alert v-if="!manifestLoadError && !manifests.length" variant="warning" show>
                        <div class="alert-body">
                            No active manifests are available. Activate a valid manifest version first.
                        </div>
                    </b-alert>

                    <div v-if="selectedManifest" class="import-runner__selection">
                        <div>
                            <div class="d-flex align-items-center mb-25">
                                <h5 class="mb-0 mr-75">{{ selectedManifest.name }}</h5>
                                <b-badge variant="light-success">Active</b-badge>
                            </div>
                            <div class="text-muted font-small-2">
                                {{ manifestTypeTitle(selectedManifest.manifestType) }}
                                · {{ selectedManifest.ownerContext }}
                                · {{ activeRevisionLabel }}
                            </div>
                            <div v-if="selectedManifest.description" class="mt-50">
                                {{ selectedManifest.description }}
                            </div>
                        </div>
                        <div v-if="loadingVersion" class="d-flex align-items-center text-muted">
                            <b-spinner small class="mr-75" />
                            Loading definition…
                        </div>
                    </div>
                </section>

                <section class="import-runner__prepare-section">
                    <div class="import-runner__section-heading">
                        <span class="import-runner__section-marker">B</span>
                        <div>
                            <h5 class="mb-25">Source file</h5>
                            <div class="text-muted font-small-2">
                                Upload and inspect the file before backend control.
                            </div>
                        </div>
                    </div>

                    <b-row class="align-items-end">
                        <b-col lg="8">
                            <b-form-group label="Source file" label-for="import-source-file">
                                <b-form-file
                                    id="import-source-file"
                                    :value="file"
                                    :accept="fileAccept"
                                    :disabled="!selectedVersion || uploading || runningControl"
                                    browse-text="Browse"
                                    :placeholder="sourceFilePlaceholder"
                                    @input="selectFile"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col lg="4">
                            <b-button
                                variant="outline-primary"
                                block
                                class="mb-1"
                                :disabled="!canUpload"
                                @click="uploadAndInspect"
                            >
                                <b-spinner v-if="uploading" small class="mr-50" />
                                {{ uploading ? 'Inspecting' : 'Upload and inspect' }}
                            </b-button>
                        </b-col>
                    </b-row>

                    <div v-if="acceptedFileTypes.length" class="text-muted font-small-2 mb-1">
                        Accepted by this manifest: {{ acceptedFileTypes.join(', ').toUpperCase() }}
                    </div>

                    <b-alert v-if="file && !fileAccepted" variant="danger" show>
                        <div class="alert-body">
                            This file type is not accepted by the selected manifest.
                        </div>
                    </b-alert>

                    <div v-if="inspection" class="import-runner__inspection">
                        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
                            <div>
                                <h5 class="mb-25">{{ inspection.filename }}</h5>
                                <div class="text-muted font-small-2">
                                    {{ formatFileSize(inspection.size) }} · {{ inspection.tabs.length }} tabs
                                </div>
                            </div>
                            <b-badge variant="light-primary">
                                {{ inspection.fileType.toUpperCase() }}
                            </b-badge>
                        </div>

                        <b-table
                            small
                            responsive
                            :items="inspectedTabs"
                            :fields="inspectionFields"
                            class="mb-0"
                        />
                    </div>
                </section>

                <div class="import-runner__prepare-footer">
                    <div class="text-muted">
                        <template v-if="inspection">
                            Source is ready. Control will map every row without changing domain data.
                        </template>
                        <template v-else>
                            Select a manifest and inspect a source file to continue.
                        </template>
                    </div>
                    <b-button
                        variant="primary"
                        :disabled="!canRunControl"
                        @click="runControl"
                    >
                        <b-spinner v-if="runningControl" small class="mr-50" />
                        {{ runningControl ? 'Running control' : 'Run control' }}
                    </b-button>
                </div>
            </b-card>

            <b-card
                v-else-if="currentStageIndex === 1 && controlRun"
                title="Control result"
                class="import-runner__control"
            >
                <ImportControlResult
                    :control-run="controlRun"
                    :manifest-name="selectedManifest ? selectedManifest.name : ''"
                    :version-label="activeRevisionLabel"
                    :source-name="inspection ? inspection.filename : ''"
                    :starting="startingJob"
                    @back="returnToPreparation"
                    @execute="startImport"
                />
            </b-card>

            <b-card
                v-else-if="importJob"
                title="Import execution"
                class="import-runner__execution"
            >
                <ImportJobProgress
                    :job="importJob"
                    :manifest-name="selectedManifest ? selectedManifest.name : ''"
                    :version-label="activeRevisionLabel"
                    :source-name="inspection ? inspection.filename : ''"
                    :refreshing="refreshingJob"
                    :poll-error="jobPollError"
                    @refresh="refreshImportJob(true)"
                    @restart="startAnotherImport"
                />
            </b-card>
        </div>
    </Overlay>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormFile,
    BFormGroup,
    BFormSelect,
    BRow,
    BSpinner,
    BTable,
} from 'bootstrap-vue'
import {ImportManifestApi} from '@core/api'
import Overlay from '@core/components/common/Overlay.vue'
import backendResponse from '@core/mixins/ui/backend-response'
import {getImportManifestTitle} from '../manifests/manifestContexts'
import ImportControlResult from './ImportControlResult.vue'
import ImportJobProgress from './ImportJobProgress.vue'

const STEPS = [
    {title: 'Prepare', description: 'Manifest and source file'},
    {title: 'Control', description: 'Validate and preview'},
    {title: 'Execution', description: 'Run and monitor'},
]

const JOB_POLL_INTERVAL = 2000
const TERMINAL_JOB_STATUSES = ['completed', 'failed']

export default {
    name: 'ImportRunner',
    components: {
        BAlert,
        BBadge,
        BButton,
        BCard,
        BCol,
        BFormFile,
        BFormGroup,
        BFormSelect,
        BRow,
        BSpinner,
        BTable,
        ImportControlResult,
        ImportJobProgress,
        Overlay,
    },
    mixins: [backendResponse],
    data() {
        return {
            activeVersion: null,
            controlRun: null,
            file: null,
            inspection: null,
            inspectionFields: [
                {key: 'name', label: 'Tab'},
                {key: 'rowCount', label: 'Rows'},
                {key: 'columnCount', label: 'Columns'},
            ],
            importJob: null,
            jobPollError: null,
            jobPollTimer: null,
            jobRequestId: 0,
            loadingManifests: false,
            loadingVersion: false,
            manifestLoadError: false,
            manifests: [],
            manifestTypeFilter: null,
            selectedManifestId: null,
            runningControl: false,
            refreshingJob: false,
            startingJob: false,
            steps: STEPS,
            uploadedFile: null,
            uploading: false,
            versionRequestId: 0,
        }
    },
    computed: {
        currentStageIndex() {
            return this.importJob ? 2 : this.controlRun ? 1 : 0
        },
        manifestTypeOptions() {
            const types = [...new Set(this.manifests.map(manifest => manifest.manifestType))]
            return [
                {value: null, text: 'All manifest types'},
                ...types.map(type => ({value: type, text: this.manifestTypeTitle(type)})),
            ]
        },
        filteredManifests() {
            return this.manifestTypeFilter
                ? this.manifests.filter(manifest => manifest.manifestType === this.manifestTypeFilter)
                : this.manifests
        },
        showManifestTypeFilter() {
            return this.manifestTypeOptions.length > 2
        },
        manifestOptions() {
            return [
                {value: null, text: 'Select an active manifest', disabled: true},
                ...this.filteredManifests.map(manifest => ({
                    value: manifest.id,
                    text: `${manifest.name} · ${this.manifestTypeTitle(manifest.manifestType)}`,
                })),
            ]
        },
        selectedManifest() {
            return this.manifests.find(manifest => manifest.id === this.selectedManifestId) || null
        },
        selectedVersion() {
            return this.activeVersion
        },
        activeRevisionLabel() {
            if (this.activeVersion) {
                return `Revision ${this.activeVersion.revision}`
            }
            return this.loadingVersion ? 'Loading…' : 'Unavailable'
        },
        acceptedFileTypes() {
            const source = this.activeVersion
                && this.activeVersion.definition
                && this.activeVersion.definition.source
            return source && Array.isArray(source.acceptedFileTypes)
                ? source.acceptedFileTypes
                : []
        },
        fileAccept() {
            return this.acceptedFileTypes.map(type => `.${type}`).join(',')
        },
        sourceFilePlaceholder() {
            if (!this.selectedManifest) {
                return 'Select a manifest first'
            }
            if (this.loadingVersion) {
                return 'Loading manifest definition'
            }
            return 'Choose a source file'
        },
        fileAccepted() {
            if (!this.file || !this.acceptedFileTypes.length) {
                return Boolean(this.file)
            }
            const extension = this.file.name.includes('.')
                ? this.file.name.split('.').pop().toLowerCase()
                : ''
            return this.acceptedFileTypes.map(type => type.toLowerCase()).includes(extension)
        },
        canUpload() {
            return Boolean(
                this.selectedManifest
                && this.selectedVersion
                && this.file
                && this.fileAccepted
                && !this.uploading
                && !this.runningControl
            )
        },
        canRunControl() {
            return Boolean(
                this.selectedManifest
                && this.uploadedFile
                && this.uploadedFile.fileReferenceId
                && this.inspection
                && !this.runningControl
                && !this.uploading
            )
        },
        inspectedTabs() {
            return this.inspection
                ? this.inspection.tabs.map(tab => ({
                    name: tab.name,
                    rowCount: tab.rowCount,
                    columnCount: tab.columns.length,
                }))
                : []
        },
        controlRunSucceeded() {
            return this.controlRun && this.controlRun.status === 'succeeded'
        },
        executionCompleted() {
            return this.importJob && this.importJob.status === 'completed'
        },
        executionFailed() {
            return this.importJob && this.importJob.status === 'failed'
        },
    },
    watch: {
        manifestTypeFilter() {
            if (
                this.selectedManifestId
                && !this.filteredManifests.some(manifest => manifest.id === this.selectedManifestId)
            ) {
                this.selectedManifestId = null
                this.resetManifestSelection()
            }
            if (!this.selectedManifestId && this.filteredManifests.length === 1) {
                this.selectedManifestId = this.filteredManifests[0].id
                this.selectManifest()
            }
        },
    },
    created() {
        this.loadManifests()
    },
    beforeDestroy() {
        this.cancelJobPolling()
    },
    methods: {
        manifestTypeTitle(type) {
            return getImportManifestTitle(type)
        },
        isRunnerStepCompleted(index) {
            return this.currentStageIndex > index || (index === 2 && this.executionCompleted)
        },
        loadManifests() {
            this.loadingManifests = true
            this.manifestLoadError = false
            ImportManifestApi.list({status: 'active'})
                .then(response => {
                    this.manifests = Array.isArray(response.data) ? response.data : []
                    if (this.manifests.length === 1) {
                        this.selectedManifestId = this.manifests[0].id
                        this.selectManifest()
                    }
                })
                .catch(error => {
                    this.manifests = []
                    this.manifestLoadError = true
                    this.handleBackendError(error)
                })
                .finally(() => {
                    this.loadingManifests = false
                })
        },
        selectManifest() {
            this.resetSource()
            this.activeVersion = null
            const manifest = this.selectedManifest
            const requestId = ++this.versionRequestId

            if (!manifest || !manifest.activeVersionId) {
                this.loadingVersion = false
                return
            }

            this.loadingVersion = true
            ImportManifestApi.getVersion(manifest.id, manifest.activeVersionId)
                .then(response => {
                    if (requestId === this.versionRequestId) {
                        this.activeVersion = response.data
                    }
                })
                .catch(error => {
                    if (requestId === this.versionRequestId) {
                        this.handleBackendError(error)
                    }
                })
                .finally(() => {
                    if (requestId === this.versionRequestId) {
                        this.loadingVersion = false
                    }
                })
        },
        resetManifestSelection() {
            this.versionRequestId += 1
            this.loadingVersion = false
            this.activeVersion = null
            this.resetSource()
        },
        resetSource() {
            this.cancelJobPolling()
            this.importJob = null
            this.jobPollError = null
            this.refreshingJob = false
            this.startingJob = false
            this.controlRun = null
            this.file = null
            this.uploadedFile = null
            this.inspection = null
        },
        selectFile(file) {
            this.controlRun = null
            this.file = file
            this.uploadedFile = null
            this.inspection = null
        },
        uploadAndInspect() {
            if (!this.canUpload) {
                return
            }

            this.uploading = true
            ImportManifestApi.uploadSourceFile(this.file)
                .then(response => {
                    this.uploadedFile = response.data
                    return ImportManifestApi.inspectSource(response.data.fileReferenceId)
                })
                .then(response => {
                    this.inspection = response.data
                    this.controlRun = null
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.uploading = false
                })
        },
        runControl() {
            if (!this.canRunControl) {
                return
            }

            this.runningControl = true
            this.controlRun = null
            ImportManifestApi.createControlRun(
                this.selectedManifest.id,
                this.uploadedFile.fileReferenceId,
                'all'
            )
                .then(response => {
                    this.controlRun = response.data
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.runningControl = false
                })
        },
        returnToPreparation() {
            this.controlRun = null
        },
        async startImport() {
            if (
                !this.controlRunSucceeded
                || !this.controlRun.id
                || this.controlRun.persisted === false
                || this.startingJob
            ) {
                return
            }

            const confirmed = await this.confirmImportExecution()
            if (!confirmed) {
                return
            }

            this.startingJob = true
            try {
                const response = await ImportManifestApi.startImportJob(this.controlRun.id)
                this.importJob = response.data
                this.jobPollError = null
                this.scheduleJobPoll()
                this.$router.push({
                    name: 'administration-import-run',
                    params: {id: response.data.id},
                })
            } catch (error) {
                this.handleBackendError(error)
            } finally {
                this.startingJob = false
            }
        },
        confirmImportExecution() {
            if (!this.$bvModal || typeof this.$bvModal.msgBoxConfirm !== 'function') {
                return Promise.resolve(true)
            }
            return this.$bvModal.msgBoxConfirm(
                'This will apply the frozen import plan to domain data. Continue?',
                {
                    title: 'Start import?',
                    okTitle: 'Start import',
                    cancelTitle: 'Cancel',
                    okVariant: 'primary',
                    cancelVariant: 'outline-secondary',
                    centered: true,
                }
            )
        },
        refreshImportJob(showLoading = false) {
            if (!this.importJob || !this.importJob.id || this.isTerminalJob(this.importJob)) {
                return Promise.resolve()
            }

            this.clearJobPollTimer()
            const requestId = ++this.jobRequestId
            if (showLoading) {
                this.refreshingJob = true
            }

            return ImportManifestApi.getImportJob(this.importJob.id)
                .then(response => {
                    if (requestId !== this.jobRequestId) {
                        return
                    }
                    this.importJob = response.data
                    this.jobPollError = null
                })
                .catch(() => {
                    if (requestId === this.jobRequestId) {
                        this.jobPollError = 'The latest progress could not be loaded. Polling will retry automatically.'
                    }
                })
                .finally(() => {
                    if (requestId !== this.jobRequestId) {
                        return
                    }
                    this.refreshingJob = false
                    this.scheduleJobPoll()
                })
        },
        scheduleJobPoll() {
            this.clearJobPollTimer()
            if (!this.importJob || this.isTerminalJob(this.importJob)) {
                return
            }
            this.jobPollTimer = window.setTimeout(() => {
                this.jobPollTimer = null
                this.refreshImportJob(false)
            }, JOB_POLL_INTERVAL)
        },
        clearJobPollTimer() {
            if (this.jobPollTimer) {
                window.clearTimeout(this.jobPollTimer)
                this.jobPollTimer = null
            }
        },
        cancelJobPolling() {
            this.clearJobPollTimer()
            this.jobRequestId += 1
        },
        isTerminalJob(job) {
            return Boolean(job && TERMINAL_JOB_STATUSES.includes(job.status))
        },
        startAnotherImport() {
            this.resetSource()
        },
        formatFileSize(bytes) {
            if (!bytes) {
                return '0 B'
            }
            if (bytes < 1024) {
                return `${bytes} B`
            }
            if (bytes < 1024 * 1024) {
                return `${(bytes / 1024).toFixed(1)} KB`
            }
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
        },
    },
}
</script>

<style lang="scss">
@import '../manifests/theme';
</style>

<style lang="scss" scoped>
.import-runner__steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: hidden;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #fff;
}

.import-runner__step {
    display: flex;
    align-items: center;
    gap: .75rem;
    padding: 1rem 1.25rem;
}

.import-runner__step + .import-runner__step {
    border-left: 1px solid #ebe9f1;
}

.import-runner__step:not(.import-runner__step--active):not(.import-runner__step--completed) {
    color: #b9b9c3;
}

.import-runner__step-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    border: 1px solid #d8d6de;
    border-radius: 50%;
}

.import-runner__step--active {
    background: rgba(255, 159, 67, .08);
}

.import-runner__step--active .import-runner__step-marker {
    border-color: #ff9f43;
    color: #fff;
    background: #ff9f43;
}

.import-runner__step--completed .import-runner__step-marker {
    border-color: #28c76f;
    color: #fff;
    background: #28c76f;
}

.import-runner__step--failed {
    background: rgba(234, 84, 85, .07);
}

.import-runner__step--failed .import-runner__step-marker {
    border-color: #ea5455;
    color: #fff;
    background: #ea5455;
}

.import-runner__prepare-section {
    padding: .5rem 0 1.5rem;
}

.import-runner__prepare-section + .import-runner__prepare-section {
    padding-top: 1.5rem;
    border-top: 1px solid #ebe9f1;
}

.import-runner__section-heading {
    display: flex;
    align-items: center;
    gap: .75rem;
    margin-bottom: 1.25rem;
}

.import-runner__section-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    color: #ff9f43;
    background: rgba(255, 159, 67, .12);
    font-weight: 600;
}

.import-runner__selection,
.import-runner__inspection {
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #f8f8f8;
}

.import-runner__selection {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.import-runner__prepare-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin: 0 -1.5rem -1.5rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #ebe9f1;
    background: #f8f8f8;
}

@media (max-width: 767.98px) {
    .import-runner__steps {
        grid-template-columns: 1fr;
    }

    .import-runner__step + .import-runner__step {
        border-top: 1px solid #ebe9f1;
        border-left: 0;
    }

    .import-runner__selection,
    .import-runner__prepare-footer {
        align-items: flex-start;
        flex-direction: column;
    }

}
</style>
